import React from "react";
import { useNavigate } from "react-router";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Stack,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PlaceIcon from "@mui/icons-material/Place";
import moment from "moment";
import { getAddress, getEventColour } from "../utils/data";

function EventCard({ event }) {
  const navigate = useNavigate();
  const isMultiDay = !moment(event.eventDate).isSame(event.endDate, "day");
  const colour = getEventColour(event.eventType);

  return (
    <Card
      key={event.eventID}
      onClick={() => navigate(`/event/${event.eventID}`)}
      sx={{
        display: "flex",
        flexDirection: "row",
        boxShadow: 2,
        borderRadius: 3,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          boxShadow: 6,
          transform: "translateY(-2px)",
          cursor: "pointer",
          backgroundColor: "#fafafa",
        },
      }}
    >
      {/* Wrap the image and chip */}
      {event.imageURL && (
        <Box sx={{ position: "relative", display: "flex" }}>
          <CardMedia
            component="img"
            sx={{
              width: { xs: 100, sm: 150 },
              minHeight: "100%",
              objectFit: "cover",
            }}
            image={event.imageURL}
            alt={event.title}
          />
          
          {/* Position the chip absolutely over the image */}
          {event.eventType && (
            <Chip
              label={event.eventType}
              size="small"
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                backgroundColor: colour,
                color: "#fff",
                fontWeight: "bold",
                borderRadius: 1.5,
                // Added a subtle shadow so it stands out against busy images
                boxShadow: "0 2px 6px rgba(0,0,0,0.25)", 
              }}
            />
          )}
        </Box>
      )}

      <CardContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          p: { xs: 2, sm: 2.5 },
          "&:last-child": { pb: { xs: 2, sm: 2.5 } }, // Override MUI default padding
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
            gap: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              lineHeight: 1.2,
              fontSize: { xs: "0.9rem", sm: "1.25rem" },
              textAlign: "left",
            }}
          >
            {event.title}
          </Typography>
          
          {/* 3. Fallback: Only render next to the title if there is NO image */}
          {!event.imageURL && event.eventType && (
            <Chip
              label={event.eventType}
              size="small"
              sx={{
                backgroundColor: colour,
                color: "#fff",
                fontWeight: "bold",
                flexShrink: 0,
              }}
            />
          )}
        </Box>

        {/* Address */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ color: "text.secondary" }}
        >
          <PlaceIcon sx={{ fontSize: "1rem", color: "#8f8f8f" }} />
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.95rem" },
              textAlign: "left"
            }}
          >
            {getAddress(event)}
          </Typography>
        </Stack>

        {/* Time / Multi-day message */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mb: 1.5, color: "text.secondary" }}
        >
          <CalendarTodayIcon sx={{ fontSize: "1rem", color: "#8f8f8f" }} />
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.95rem" },
              textAlign: "left"
            }}
          >
            {isMultiDay
              ? `All day, ends ${moment(event.endDate).format("DD/MM/YYYY")}`
              : `${moment(event.eventDate).format("h:mm A")} - ${moment(event.endDate).format("h:mm A")}`}
          </Typography>
        </Stack>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: { xs: "0.75rem" },
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            textAlign: "left",
          }}
        >
          {event.description}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default EventCard;